import React, { useState } from 'react';
import {
  X,
  Send,
  Paperclip,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Palette,
  Eye,
  FileText,
  Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';

const emailTemplates = [
  { id: 'intro', name: 'Introduction Email', subject: 'Introduction to {{company_name}} HRMS' },
  { id: 'followup', name: 'Follow Up', subject: 'Following up on our conversation' },
  { id: 'demo_invite', name: 'Demo Invitation', subject: 'Your personalized demo invitation' },
  { id: 'proposal', name: 'Proposal Email', subject: 'Proposal for {{company_name}}' },
];

const EmailComposer = ({ isOpen, onClose, recipientEmail, recipientName, companyName }) => {
  const [emailData, setEmailData] = useState({
    to: recipientEmail || '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: []
  });
  
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);

  const applyTemplate = (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      const subject = template.subject
        .replace('{{company_name}}', companyName || 'Your Company');
      
      let body = '';
      switch (templateId) {
        case 'intro':
          body = `Dear ${recipientName || 'Sir/Madam'},\n\nI hope this email finds you well. I am reaching out to introduce our comprehensive HRMS solution that can help ${companyName || 'your organization'} streamline HR operations.\n\nOur platform offers:\n- Complete employee lifecycle management\n- Automated payroll processing\n- Leave and attendance management\n- Performance management\n- And much more...\n\nWould you be available for a quick 15-minute call to discuss how we can help?\n\nBest regards`;
          break;
        case 'followup':
          body = `Dear ${recipientName || 'Sir/Madam'},\n\nI wanted to follow up on our recent conversation about your HR management needs.\n\nI understand you mentioned concerns about [specific concern]. I'd like to address that and show you how our solution handles it effectively.\n\nAre you available for a brief call this week?\n\nLooking forward to hearing from you.\n\nBest regards`;
          break;
        case 'demo_invite':
          body = `Dear ${recipientName || 'Sir/Madam'},\n\nThank you for your interest in our HRMS solution!\n\nI'd like to invite you to a personalized demo where I can show you:\n\n1. How our platform can automate your current HR processes\n2. Specific features relevant to ${companyName || 'your industry'}\n3. Implementation timeline and support\n\nPlease let me know your preferred date and time, or feel free to book directly using the link below.\n\nBest regards`;
          break;
        case 'proposal':
          body = `Dear ${recipientName || 'Sir/Madam'},\n\nPlease find attached our detailed proposal for ${companyName || 'your organization'}.\n\nThe proposal includes:\n- Recommended plan and pricing\n- Implementation roadmap\n- Support and training details\n- Terms and conditions\n\nI'm available to discuss any questions you may have.\n\nBest regards`;
          break;
        default:
          body = '';
      }
      
      setEmailData({
        ...emailData,
        subject,
        body
      });
      setSelectedTemplate(templateId);
    }
  };

  const handleAddAttachment = () => {
    // Mock adding attachment
    const mockFile = { name: 'Proposal.pdf', size: '1.2 MB', id: Date.now() };
    setEmailData({
      ...emailData,
      attachments: [...emailData.attachments, mockFile]
    });
  };

  const handleRemoveAttachment = (id) => {
    setEmailData({
      ...emailData,
      attachments: emailData.attachments.filter(a => a.id !== id)
    });
  };

  const handleSend = () => {
    console.log('Sending email:', emailData);
    onClose();
  };

  const formatText = (format) => {
    // Simple text formatting simulation
    console.log('Applying format:', format);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Compose Email
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={isPreviewMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
            >
              <Eye className="w-4 h-4 mr-1" />
              {isPreviewMode ? 'Edit' : 'Preview'}
            </Button>
          </div>
        </div>

        <div className="flex flex-col max-h-[calc(90vh-140px)] overflow-hidden">
          {!isPreviewMode ? (
            <>
              {/* Email Fields */}
              <div className="p-4 space-y-3 border-b dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Label className="w-16 text-sm text-slate-500">To:</Label>
                  <Input
                    value={emailData.to}
                    onChange={(e) => setEmailData({...emailData, to: e.target.value})}
                    placeholder="recipient@example.com"
                    className="flex-1"
                  />
                  <Button variant="ghost" size="sm" onClick={() => setShowCcBcc(!showCcBcc)}>
                    {showCcBcc ? 'Hide' : 'Cc/Bcc'}
                  </Button>
                </div>
                
                {showCcBcc && (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="w-16 text-sm text-slate-500">Cc:</Label>
                      <Input
                        value={emailData.cc}
                        onChange={(e) => setEmailData({...emailData, cc: e.target.value})}
                        placeholder="cc@example.com"
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="w-16 text-sm text-slate-500">Bcc:</Label>
                      <Input
                        value={emailData.bcc}
                        onChange={(e) => setEmailData({...emailData, bcc: e.target.value})}
                        placeholder="bcc@example.com"
                        className="flex-1"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center gap-2">
                  <Label className="w-16 text-sm text-slate-500">Subject:</Label>
                  <Input
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    placeholder="Email subject"
                    className="flex-1"
                  />
                </div>

                {/* Template Selector */}
                <div className="flex items-center gap-2">
                  <Label className="w-16 text-sm text-slate-500">Template:</Label>
                  <Select value={selectedTemplate} onValueChange={applyTemplate}>
                    <SelectTrigger className="w-[250px]">
                      <SelectValue placeholder="Choose a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Formatting Toolbar */}
              <div className="flex items-center gap-1 p-2 border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => formatText('bold')}>
                  <Bold className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => formatText('italic')}>
                  <Italic className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => formatText('underline')}>
                  <Underline className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <List className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <ListOrdered className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <AlignLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <AlignCenter className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <AlignRight className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Link className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Image className="w-4 h-4" />
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <Type className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-32 p-2">
                    <div className="space-y-1">
                      <button className="w-full text-left px-2 py-1 text-xs hover:bg-slate-100 rounded">Small</button>
                      <button className="w-full text-left px-2 py-1 text-sm hover:bg-slate-100 rounded">Normal</button>
                      <button className="w-full text-left px-2 py-1 text-lg hover:bg-slate-100 rounded">Large</button>
                      <button className="w-full text-left px-2 py-1 text-xl hover:bg-slate-100 rounded">Heading</button>
                    </div>
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <Palette className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 p-2">
                    <div className="flex flex-wrap gap-1">
                      {['#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777'].map(color => (
                        <button
                          key={color}
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: color }}
                          onClick={() => formatText(`color:${color}`)}
                        />
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Email Body */}
              <div className="flex-1 p-4 overflow-auto">
                <Textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                  placeholder="Compose your email..."
                  className="min-h-[250px] resize-none border-0 focus-visible:ring-0 p-0"
                />
              </div>

              {/* Attachments */}
              {emailData.attachments.length > 0 && (
                <div className="px-4 py-2 border-t dark:border-slate-700">
                  <div className="flex flex-wrap gap-2">
                    {emailData.attachments.map(file => (
                      <Badge key={file.id} variant="outline" className="gap-2 py-1">
                        <Paperclip className="w-3 h-3" />
                        {file.name} ({file.size})
                        <button onClick={() => handleRemoveAttachment(file.id)} className="text-red-500 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Preview Mode */
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-6">
                <div className="border-b dark:border-slate-700 pb-4 mb-4">
                  <p className="text-sm text-slate-500">To: <span className="text-slate-800 dark:text-slate-200">{emailData.to}</span></p>
                  {emailData.cc && <p className="text-sm text-slate-500">Cc: <span className="text-slate-800 dark:text-slate-200">{emailData.cc}</span></p>}
                  <p className="text-lg font-semibold mt-2 dark:text-slate-100">{emailData.subject || '(No Subject)'}</p>
                </div>
                <div className="whitespace-pre-line text-slate-700 dark:text-slate-300">
                  {emailData.body || '(No content)'}
                </div>
                {emailData.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t dark:border-slate-700">
                    <p className="text-sm text-slate-500 mb-2">Attachments:</p>
                    <div className="flex flex-wrap gap-2">
                      {emailData.attachments.map(file => (
                        <Badge key={file.id} variant="outline">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {file.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleAddAttachment}>
              <Paperclip className="w-4 h-4 mr-1" />
              Attach
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSend} className="bg-blue-600 hover:bg-blue-700" disabled={!emailData.to || !emailData.subject}>
              <Send className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailComposer;
